import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Connects to data-controller="custom-timeline"
export default class extends Controller {
  static targets = ["phaseContainer", "submitButton", "resetButton", "addPhaseButton", "tierGateModal"]
  static values = {
    eventDate: String,
    originalPhases: Array,
    canAdd: Boolean,
    canDelete: Boolean
  }

  connect() {
    this.updatePhaseNumbers()
    this.validateForm()
    this.initSortable()
  }

  disconnect() {
    if (this.phaseSortable) {
      this.phaseSortable.destroy()
      this.phaseSortable = null
    }
    this.taskSortables.forEach(sortable => sortable.destroy())
    this.taskSortables = []
  }

  initSortable() {
    this.taskSortables = []

    // Phase-level sortable
    this.phaseSortable = Sortable.create(this.phaseContainerTarget, {
      handle: '.phase-drag-handle',
      draggable: '.phase-item',
      animation: 150,
      ghostClass: 'opacity-50',
      onEnd: () => {
        this.updatePhaseNumbers()
        this.validateForm()
      }
    })

    // Task-level sortables for each existing phase
    this.phaseContainerTarget.querySelectorAll('.tasks-container').forEach(container => {
      this.initTaskSortable(container)
    })
  }

  initTaskSortable(tasksContainer) {
    const sortable = Sortable.create(tasksContainer, {
      handle: '.task-drag-handle',
      draggable: '.task-item',
      animation: 150,
      ghostClass: 'opacity-50',
      group: false,
      onEnd: () => {
        const phaseItem = tasksContainer.closest('.phase-item')
        this.updateTaskIndexes(phaseItem)
        this.updateTaskCount(phaseItem)
        this.validateForm()
      }
    })
    this.taskSortables.push(sortable)
  }

  // Add a new phase
  addPhase(event) {
    event.preventDefault()

    if (!this.canAddValue) {
      this.openTierGateModal()
      return
    }

    const phaseContainer = this.phaseContainerTarget
    const phaseCount = phaseContainer.children.length

    const newPhaseHtml = this.createPhaseHtml(phaseCount + 1, {
      phase: `Phase ${phaseCount + 1}`,
      timeframe: '2 weeks before event',
      tasks: ['New task']
    })

    phaseContainer.insertAdjacentHTML('beforeend', newPhaseHtml)
    this.updatePhaseNumbers()
    this.validateForm()

    // Init task sortable for the new phase
    const newPhase = phaseContainer.lastElementChild
    const tasksContainer = newPhase.querySelector('.tasks-container')
    if (tasksContainer) {
      this.initTaskSortable(tasksContainer)
    }

    // Focus on the new phase name input
    const nameInput = newPhase.querySelector('input[name*="[phase]"]')
    if (nameInput) {
      nameInput.focus()
      nameInput.select()
    }
  }

  // Remove a phase
  removePhase(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.canDeleteValue) {
      this.openTierGateModal()
      return
    }

    const phaseItem = event.target.closest('.phase-item')
    if (!phaseItem) return

    const phaseContainer = this.phaseContainerTarget
    if (phaseContainer.children.length <= 1) {
      alert('You must have at least one phase in your timeline.')
      return
    }

    if (confirm('Are you sure you want to remove this phase?')) {
      phaseItem.remove()
      this.updatePhaseNumbers()
      this.validateForm()
    }
  }

  // Move phase up
  movePhaseUp(event) {
    event.preventDefault()

    const phaseItem = event.target.closest('.phase-item')
    const previousSibling = phaseItem.previousElementSibling

    if (previousSibling) {
      phaseItem.parentNode.insertBefore(phaseItem, previousSibling)
      this.updatePhaseNumbers()
      this.validateForm()
    }
  }

  // Move phase down
  movePhaseDown(event) {
    event.preventDefault()

    const phaseItem = event.target.closest('.phase-item')
    const nextSibling = phaseItem.nextElementSibling

    if (nextSibling) {
      phaseItem.parentNode.insertBefore(nextSibling, phaseItem)
      this.updatePhaseNumbers()
      this.validateForm()
    }
  }

  // Add task to a phase
  addTask(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.canAddValue) {
      this.openTierGateModal()
      return
    }

    const phaseItem = event.target.closest('.phase-item')
    const tasksContainer = phaseItem.querySelector('.tasks-container')
    const taskCount = tasksContainer.children.length
    const phaseIndex = Array.from(this.phaseContainerTarget.children).indexOf(phaseItem)

    const newTaskHtml = this.createTaskHtml(phaseIndex, taskCount)

    tasksContainer.insertAdjacentHTML('beforeend', newTaskHtml)

    // Focus on the new task input
    const newTaskInput = tasksContainer.lastElementChild.querySelector('input')
    if (newTaskInput) {
      newTaskInput.focus()
      newTaskInput.select()
    }

    this.updateTaskIndexes(phaseItem)
    this.updateTaskCount(phaseItem)
    this.validateForm()
  }

  // Remove task from a phase
  removeTask(event) {
    event.preventDefault()

    if (!this.canDeleteValue) {
      this.openTierGateModal()
      return
    }

    const taskItem = event.target.closest('.task-item')
    const phaseItem = event.target.closest('.phase-item')
    const tasksContainer = phaseItem.querySelector('.tasks-container')

    if (tasksContainer.children.length <= 1) {
      alert('Each phase must have at least one task.')
      return
    }

    taskItem.remove()
    this.updateTaskIndexes(phaseItem)
    this.updateTaskCount(phaseItem)
    this.validateForm()
  }

  // Update the "TASK ITEMS (N)" label for a phase
  updateTaskCount(phaseElement) {
    const taskCount = phaseElement.querySelectorAll('.task-item').length
    const label = phaseElement.querySelector('.task-count-label')
    if (label) {
      label.textContent = `Task Items (${taskCount})`
    }
  }

  // Validate the entire form
  validateForm() {
    const phases = Array.from(this.phaseContainerTarget.children)
    let isValid = true
    const errors = []

    // Check if we have at least one phase
    if (phases.length === 0) {
      errors.push('Timeline must have at least one phase')
      isValid = false
    }

    phases.forEach((phaseItem, index) => {
      // Check phase name
      const nameInput = phaseItem.querySelector('input[name*="[phase]"]')
      if (!nameInput || !nameInput.value.trim()) {
        errors.push(`Phase ${index + 1} must have a name`)
        isValid = false
      }

      // Check timeframe
      const timeframeInput = phaseItem.querySelector('input[name*="[timeframe]"]')
      if (!timeframeInput || !timeframeInput.value.trim()) {
        errors.push(`Phase ${index + 1} must have a timeframe`)
        isValid = false
      }

      // Check tasks
      const taskInputs = phaseItem.querySelectorAll('.task-item input')
      if (taskInputs.length === 0) {
        errors.push(`Phase ${index + 1} must have at least one task`)
        isValid = false
      } else {
        taskInputs.forEach((taskInput, taskIndex) => {
          if (!taskInput.value.trim()) {
            errors.push(`Phase ${index + 1}, Task ${taskIndex + 1} cannot be empty`)
            isValid = false
          }
        })
      }
    })

    // Check for duplicate phase names
    const phaseNames = phases.map(phase => {
      const nameInput = phase.querySelector('input[name*="[phase]"]')
      return nameInput ? nameInput.value.trim().toLowerCase() : ''
    }).filter(name => name)

    const uniqueNames = [...new Set(phaseNames)]
    if (phaseNames.length !== uniqueNames.length) {
      errors.push('Phase names must be unique')
      isValid = false
    }

    // Update submit button state
    this.submitButtonTargets.forEach(button => {
      button.disabled = !isValid
      button.classList.toggle('opacity-50', !isValid)
      button.classList.toggle('cursor-not-allowed', !isValid)
    })

    // Show validation messages
    this.showValidationMessages(errors)

    return isValid
  }

  // Show validation messages using DOM construction instead of innerHTML
  showValidationMessages(errors) {
    const messageContainer = this.element.querySelector('#timeline-validation-messages')
    if (!messageContainer) return

    messageContainer.innerHTML = ''

    if (errors.length > 0) {
      const wrapper = document.createElement('div')
      wrapper.className = 'bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800'

      const icon = document.createElement('i')
      icon.className = 'ph-fill ph-warning mr-2'
      wrapper.appendChild(icon)

      wrapper.appendChild(document.createTextNode('Please fix the following issues:'))

      const list = document.createElement('ul')
      list.className = 'list-disc list-inside mt-2 space-y-1'

      errors.forEach(error => {
        const li = document.createElement('li')
        li.textContent = error
        list.appendChild(li)
      })

      wrapper.appendChild(list)
      messageContainer.appendChild(wrapper)
    }
  }

  // Update phase numbers in the UI
  updatePhaseNumbers() {
    const phases = this.phaseContainerTarget.children
    Array.from(phases).forEach((phaseItem, index) => {
      // Update the numbered circle
      const numberElement = phaseItem.querySelector('.phase-number')
      if (numberElement) {
        numberElement.textContent = index + 1
      }

      // Update the "Phase N" label
      const phaseLabel = phaseItem.querySelector('.phase-label')
      if (phaseLabel) {
        phaseLabel.textContent = `Phase ${index + 1}`
      }

      // Update form field names for proper indexing
      this.updatePhaseIndexes(phaseItem, index)

      // Update task count
      this.updateTaskCount(phaseItem)
    })
  }

  // Update phase indexes in form fields
  updatePhaseIndexes(phaseItem, phaseIndex) {
    // Update phase name input
    const nameInput = phaseItem.querySelector('input[name*="[phase]"]')
    if (nameInput) {
      nameInput.name = `custom_event_plan[custom_timeline][planning_phases][${phaseIndex}][phase]`
    }

    // Update timeframe input
    const timeframeInput = phaseItem.querySelector('input[name*="[timeframe]"]')
    if (timeframeInput) {
      timeframeInput.name = `custom_event_plan[custom_timeline][planning_phases][${phaseIndex}][timeframe]`
    }

    // Update task inputs
    this.updateTaskIndexes(phaseItem)
  }

  // Update task indexes within a phase
  updateTaskIndexes(phaseItem) {
    const phaseIndex = Array.from(this.phaseContainerTarget.children).indexOf(phaseItem)
    const taskInputs = phaseItem.querySelectorAll('.task-item input')

    taskInputs.forEach((taskInput, taskIndex) => {
      taskInput.name = `custom_event_plan[custom_timeline][planning_phases][${phaseIndex}][tasks][${taskIndex}]`
    })
  }

  // Escape HTML to prevent XSS
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Create HTML for a new task
  createTaskHtml(phaseIndex, taskIndex) {
    const deleteButton = this.canDeleteValue
      ? `<button type="button"
                class="text-gray-400 hover:text-red-500 p-1 transition-colors"
                data-action="click->custom-timeline#removeTask"
                title="Remove task">
          <i class="ph ph-x-circle text-lg"></i>
        </button>`
      : `<button type="button"
                class="text-gray-400 opacity-60 p-1"
                data-action="click->custom-timeline#openTierGateModal"
                title="Upgrade to remove tasks">
          <i class="ph ph-lock-simple text-lg"></i>
        </button>`

    return `
      <div class="task-item flex items-center gap-2">
        <i class="ph ph-dots-six-vertical text-gray-400 flex-shrink-0 cursor-grab task-drag-handle"></i>
        <input type="text"
               name="custom_event_plan[custom_timeline][planning_phases][${phaseIndex}][tasks][${taskIndex}]"
               value="New task"
               class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
               data-action="input->custom-timeline#validateForm"
               placeholder="Enter task description..." />
        ${deleteButton}
      </div>
    `
  }

  // Create HTML for a new phase
  createPhaseHtml(phaseNumber, phaseData) {
    const escapedPhase = this.escapeHtml(phaseData.phase)
    const escapedTimeframe = this.escapeHtml(phaseData.timeframe)

    const taskDeleteButton = this.canDeleteValue
      ? `<button type="button"
                class="text-gray-400 hover:text-red-500 p-1 transition-colors"
                data-action="click->custom-timeline#removeTask"
                title="Remove task">
          <i class="ph ph-x-circle text-lg"></i>
        </button>`
      : `<button type="button"
                class="text-gray-400 opacity-60 p-1"
                data-action="click->custom-timeline#openTierGateModal"
                title="Upgrade to remove tasks">
          <i class="ph ph-lock-simple text-lg"></i>
        </button>`

    const tasksHtml = phaseData.tasks.map((task, taskIndex) => {
      const escapedTask = this.escapeHtml(task)
      return `
        <div class="task-item flex items-center gap-2">
          <i class="ph ph-dots-six-vertical text-gray-400 flex-shrink-0 cursor-grab task-drag-handle"></i>
          <input type="text"
                 name="custom_event_plan[custom_timeline][planning_phases][${phaseNumber - 1}][tasks][${taskIndex}]"
                 value="${escapedTask}"
                 class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                 data-action="input->custom-timeline#validateForm"
                 placeholder="Enter task description..." />
          ${taskDeleteButton}
        </div>
      `
    }).join('')

    const phaseTrashButton = this.canDeleteValue
      ? `<button type="button"
                class="text-red-400 hover:text-red-600 p-1 transition-colors"
                data-action="click->custom-timeline#removePhase"
                onclick="event.stopPropagation()"
                title="Remove phase">
          <i class="ph ph-trash text-base"></i>
        </button>`
      : `<button type="button"
                class="text-gray-400 opacity-60 p-1"
                data-action="click->custom-timeline#openTierGateModal"
                onclick="event.stopPropagation()"
                title="Upgrade to remove phases">
          <i class="ph ph-lock-simple text-base"></i>
        </button>`

    const addTaskButton = this.canAddValue
      ? `<button type="button"
                class="text-teal-600 hover:text-teal-800 text-xs font-semibold flex items-center transition-colors"
                data-action="click->custom-timeline#addTask"
                onclick="event.stopPropagation()">
          <i class="ph ph-plus mr-1"></i>Add Task
        </button>`
      : `<button type="button"
                class="text-gray-400 text-xs font-semibold flex items-center opacity-60"
                data-action="click->custom-timeline#openTierGateModal"
                onclick="event.stopPropagation()">
          <i class="ph ph-lock-simple mr-1"></i>Add Task
          <span class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-teal-50 text-teal-700 ring-1 ring-teal-600/20 ml-1.5">Plus</span>
        </button>`

    return `
      <div class="phase-item border border-gray-200 rounded-lg overflow-hidden"
           data-controller="collapsible"
           data-collapsible-open-value="true">
        <div class="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
             data-action="click->collapsible#toggle">
          <i class="ph ph-dots-six-vertical text-gray-400 flex-shrink-0 cursor-grab phase-drag-handle" onclick="event.stopPropagation()"></i>
          <div class="phase-number w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 border-2 border-[#5b9fbf] text-[#5b9fbf]">
            ${phaseNumber}
          </div>
          <span class="phase-label text-sm font-semibold text-gray-900 flex-1">Phase ${phaseNumber}</span>
          ${phaseTrashButton}
          <i class="ph-fill ph-caret-down text-gray-400 transition-transform duration-300"
             data-collapsible-target="icon"
             style="transform: rotate(180deg)"></i>
        </div>

        <div data-collapsible-target="content" class="px-4 pb-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-1.5">Phase Name <span class="text-red-400">*</span></label>
              <input type="text"
                     name="custom_event_plan[custom_timeline][planning_phases][${phaseNumber - 1}][phase]"
                     value="${escapedPhase}"
                     class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                     data-action="input->custom-timeline#validateForm"
                     placeholder="e.g., Initial Planning & Booking" />
            </div>
            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-1.5">Coverage Period <span class="text-red-400">*</span></label>
              <input type="text"
                     name="custom_event_plan[custom_timeline][planning_phases][${phaseNumber - 1}][timeframe]"
                     value="${escapedTimeframe}"
                     class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                     data-action="input->custom-timeline#validateForm"
                     placeholder="e.g., 2-3 months before event" />
            </div>
          </div>

          <div>
            <div class="flex items-center justify-between mb-2">
              <span class="task-count-label text-xs font-semibold uppercase tracking-wide text-gray-500">
                Task Items (${phaseData.tasks.length})
              </span>
              ${addTaskButton}
            </div>
            <div class="tasks-container space-y-2">
              ${tasksHtml}
            </div>
          </div>
        </div>
      </div>
    `
  }

  // Reset to original AI timeline
  resetToAI(event) {
    event.preventDefault()

    if (confirm('Are you sure you want to reset the timeline to AI recommendations? All your changes will be lost.')) {
      // Find and submit the reset form
      const form = this.element.querySelector('#reset-timeline-form')
      if (form) {
        form.requestSubmit()
      }
    }
  }

  // Handle form submission
  submitForm(event) {
    if (!this.validateForm()) {
      event.preventDefault()
      return false
    }

    return true
  }

  // Open the tier gate upgrade modal
  openTierGateModal() {
    if (this.hasTierGateModalTarget) {
      this.tierGateModalTarget.classList.remove('hidden')
    }
  }

  // Close the tier gate upgrade modal
  closeTierGateModal() {
    if (this.hasTierGateModalTarget) {
      this.tierGateModalTarget.classList.add('hidden')
    }
  }

  // Prevent clicks inside modal from closing it
  stopModalPropagation(event) {
    event.stopPropagation()
  }
}
